'use client';

/**
 * useDevicePerformance Hook
 * =========================
 *
 * Detects device performance capabilities and returns appropriate settings.
 * Used to disable heavy animations on low-end devices for smooth experience.
 *
 * Detection methods:
 * 1. Hardware concurrency (CPU cores)
 * 2. Device memory (RAM)
 * 3. GPU renderer string (WebGL)
 * 4. User preference (reduced motion)
 * 5. Connection type (slow network = likely slow device)
 *
 * Tiers:
 * - HIGH: Modern devices, all effects enabled
 * - MEDIUM: Mid-range, reduced effects
 * - LOW: Old/weak devices, minimal effects
 */

import { useEffect, useState } from 'react';

export type PerformanceTier = 'high' | 'medium' | 'low';

interface PerformanceConfig {
  tier: PerformanceTier;
  enableLiquidEther: boolean;
  enableVideoAutoplay: boolean;
  enableScrollAnimations: boolean;
  enableParallax: boolean;
  videoQuality: 'high' | 'medium' | 'low';
  liquidEtherResolution: number;
  reducedMotion: boolean;
  // New LiquidEther optimization settings
  liquidEtherFPS: number;
  liquidEtherBFECC: boolean;
  liquidEtherViscous: boolean;
  liquidEtherIterations: number;
}

// Known low-performance GPU patterns
const LOW_PERF_GPU_PATTERNS = [
  /intel.*hd graphics/i,
  /intel.*uhd graphics [0-9]{3}$/i, // Intel UHD 620, etc (not newer ones)
  /intel.*iris/i,
  /radeon.*pro 555/i,
  /radeon.*pro 560/i,
  /radeon.*pro 5[0-3]/i, // Older Radeon Pro
  /mali/i,
  /adreno [0-5]/i, // Older Adreno
  /powervr/i,
  /apple gpu/i, // Generic Apple GPU (older)
  /swiftshader/i, // Software renderer
  /llvmpipe/i, // Software renderer
  /microsoft basic/i, // Software renderer
];

// Medium performance GPUs
const MEDIUM_PERF_GPU_PATTERNS = [
  /radeon.*pro 56/i,
  /radeon.*rx 5[0-6]/i,
  /geforce.*gt/i,
  /geforce.*mx/i,
  /geforce.*[0-9]{3}$/i, // GTX 960, etc
  /intel.*uhd graphics [0-9]{4}/i, // Newer Intel UHD
];

function getGPUTier(): PerformanceTier {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (!gl) return 'low';

    const debugInfo = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
    if (!debugInfo) return 'medium';

    const renderer = (gl as WebGLRenderingContext).getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);

    // Free the probe context right away - browsers cap live WebGL contexts
    (gl as WebGLRenderingContext).getExtension('WEBGL_lose_context')?.loseContext();

    // Check for low-perf GPUs
    for (const pattern of LOW_PERF_GPU_PATTERNS) {
      if (pattern.test(renderer)) return 'low';
    }

    // Check for medium-perf GPUs
    for (const pattern of MEDIUM_PERF_GPU_PATTERNS) {
      if (pattern.test(renderer)) return 'medium';
    }

    return 'high';
  } catch {
    return 'medium';
  }
}

function getCPUTier(): PerformanceTier {
  const cores = navigator.hardwareConcurrency || 4;

  if (cores <= 2) return 'low';
  if (cores <= 4) return 'medium';
  return 'high';
}

function getMemoryTier(): PerformanceTier {
  // @ts-expect-error - deviceMemory is not in all browsers
  const memory = navigator.deviceMemory;

  if (!memory) return 'medium'; // Unknown, assume medium
  if (memory <= 2) return 'low';
  if (memory <= 4) return 'medium';
  return 'high';
}

function getConnectionTier(): PerformanceTier {
  // @ts-expect-error - connection is not in all browsers
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  if (!connection) return 'high';

  const effectiveType = connection.effectiveType;
  if (effectiveType === 'slow-2g' || effectiveType === '2g') return 'low';
  if (effectiveType === '3g') return 'medium';
  return 'high';
}

function calculateOverallTier(tiers: PerformanceTier[]): PerformanceTier {
  const scores = { low: 0, medium: 1, high: 2 };
  const total = tiers.reduce((sum, tier) => sum + scores[tier], 0);
  const avg = total / tiers.length;

  // Be conservative - if average is below medium, go low
  if (avg < 0.8) return 'low';
  if (avg < 1.5) return 'medium';
  return 'high';
}

export function useDevicePerformance(): PerformanceConfig {
  const [config, setConfig] = useState<PerformanceConfig>({
    tier: 'high',
    enableLiquidEther: true,
    enableVideoAutoplay: true,
    enableScrollAnimations: true,
    enableParallax: true,
    videoQuality: 'high',
    liquidEtherResolution: 0.35,
    reducedMotion: false,
    // Optimized defaults for LiquidEther
    liquidEtherFPS: 30,
    liquidEtherBFECC: false,
    liquidEtherViscous: false,
    liquidEtherIterations: 12,
  });

  useEffect(() => {
    // Check reduced motion preference first
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      setConfig({
        tier: 'low',
        enableLiquidEther: false,
        enableVideoAutoplay: true,
        enableScrollAnimations: false,
        enableParallax: false,
        videoQuality: 'medium',
        liquidEtherResolution: 0,
        reducedMotion: true,
        liquidEtherFPS: 0,
        liquidEtherBFECC: false,
        liquidEtherViscous: false,
        liquidEtherIterations: 0,
      });
      return;
    }

    // Creating a WebGL context to read the GPU name is slow (tens of ms, more
    // on low-end phones). Run detection when the browser is idle instead of
    // during hydration so it doesn't add to Total Blocking Time.
    const detect = () => {
    // Gather performance metrics
    const gpuTier = getGPUTier();
    const cpuTier = getCPUTier();
    const memoryTier = getMemoryTier();
    const connectionTier = getConnectionTier();

    const overallTier = calculateOverallTier([gpuTier, cpuTier, memoryTier, connectionTier]);

    // Log for debugging (development only)
    if (process.env.NODE_ENV === 'development') console.log('[Performance Detection]', {
      gpu: gpuTier,
      cpu: cpuTier,
      memory: memoryTier,
      connection: connectionTier,
      overall: overallTier,
    });

    // Configure based on tier
    switch (overallTier) {
      case 'low':
        setConfig({
          tier: 'low',
          enableLiquidEther: false, // Disable completely
          enableVideoAutoplay: true, // Still autoplay but poster fallback
          enableScrollAnimations: false, // Disable GSAP animations
          enableParallax: false,
          videoQuality: 'low',
          liquidEtherResolution: 0,
          reducedMotion: false,
          liquidEtherFPS: 0,
          liquidEtherBFECC: false,
          liquidEtherViscous: false,
          liquidEtherIterations: 0,
        });
        break;

      case 'medium':
        setConfig({
          tier: 'medium',
          enableLiquidEther: true,
          enableVideoAutoplay: true,
          enableScrollAnimations: true,
          enableParallax: false, // Disable parallax (expensive)
          videoQuality: 'medium',
          liquidEtherResolution: 0.2,  // PERF: Very low resolution
          reducedMotion: false,
          liquidEtherFPS: 24,          // PERF: 24fps for medium devices
          liquidEtherBFECC: false,     // PERF: Disabled
          liquidEtherViscous: false,   // PERF: Disabled
          liquidEtherIterations: 8,    // PERF: Minimal iterations
        });
        break;

      case 'high':
      default:
        setConfig({
          tier: 'high',
          enableLiquidEther: true,
          enableVideoAutoplay: true,
          enableScrollAnimations: true,
          enableParallax: true,
          videoQuality: 'high',
          liquidEtherResolution: 0.35, // PERF: Still optimized (was 0.5)
          reducedMotion: false,
          liquidEtherFPS: 30,          // PERF: 30fps is smooth enough
          liquidEtherBFECC: false,     // PERF: Disabled (minimal visual diff)
          liquidEtherViscous: false,   // PERF: Disabled
          liquidEtherIterations: 12,   // PERF: Reduced from 32
        });
        break;
    }
    };

    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(detect, { timeout: 3000 });
      return () => window.cancelIdleCallback(idleId);
    }
    const timeoutId = setTimeout(detect, 1000);
    return () => clearTimeout(timeoutId);
  }, []);

  return config;
}

export default useDevicePerformance;
